import { ProductService } from '../product';
import { IProduct, IProductReview, IProductReviewed } from '@monofront/types';
import { Channel } from '../../constants';

const PRIVATE_LABEL_TAG = 'Marques_La Fourche';

export class ProductReviewsService {
  private productService: ProductService;

  constructor(productService) {
    this.productService = productService;
  }

  private sortByReviewStats = (products: IProduct[]) => {
    const productsSorted = products.sort((a, b) => {
      const aReviewsCount = a.meta.reviews_stats?.global.count || 0;
      const bReviewsCount = b.meta.reviews_stats?.global.count || 0;
      const aIncludesPrivateLabel = a.tags.includes(PRIVATE_LABEL_TAG);
      const bIncludesPrivateLabel = b.tags.includes(PRIVATE_LABEL_TAG);

      if (
        (!aIncludesPrivateLabel && !bIncludesPrivateLabel) ||
        (aIncludesPrivateLabel && bIncludesPrivateLabel)
      ) {
        return aReviewsCount - bReviewsCount;
      }

      if (aReviewsCount === bReviewsCount) {
        if (aIncludesPrivateLabel && !bIncludesPrivateLabel) {
          return -1;
        } else if (!aIncludesPrivateLabel && bIncludesPrivateLabel) {
          return 1;
        }

        return 0;
      }

      return aReviewsCount - bReviewsCount;
    });

    return productsSorted.filter((product) => product.published_at);
  };

  public getProductsSorted = async (
    productsSku: string[],
    productReviewsByCustomer: IProductReview[],
    channel = Channel.FR,
  ): Promise<{
    productsToReview: IProduct[];
    productsReviewed: IProductReviewed[];
    channel?: Channel;
  }> => {
    const products: IProduct[] = await this.productService.getProductsBySku(
      productsSku,
      channel as Channel,
    );
    const productReviewsByCustomerSku = productReviewsByCustomer.map(
      ({ reviewSubject }) => reviewSubject.code,
    );
    const productsToReview = products.filter(
      ({ sku }) => !productReviewsByCustomerSku.includes(sku),
    );
    const productsToReviewSorted = this.sortByReviewStats(productsToReview);

    const productsReviewed: IProductReviewed[] = productReviewsByCustomer
      .filter((review) => productsSku.includes(review.reviewSubject.code))
      .map((review) => {
        const product = products.find(
          (product) => product.sku === review.reviewSubject.code,
        )!;

        return {
          ...review,
          image: product.image,
          handle: product.handle,
          finance: product.meta?.finance,
          productTitle: product.title,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .filter(
        (review, index, reviews) =>
          reviews.findIndex(
            (reviewFind) =>
              reviewFind.reviewSubject.code === review.reviewSubject.code,
          ) === index,
      );

    return {
      productsToReview: productsToReviewSorted,
      productsReviewed,
    };
  };
}
